import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { SIRUC_RECORDS } from "@/constants/testIds";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Truck, ClipboardCheck, Camera, FileDown, Trash2, ExternalLink } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { PhotoCapture } from "@/components/PhotoCapture";

const GUARDIA_OPCIONES = ["MARIO AGUILAR", "ADELAIDO SAENZ"];
const CONDICIONES_SALIDA = ["VACIA", "CARGADA", "CONSOLIDADO", "OTRA"];

const EMPTY_EXIT = {
  cortina_salida: "",
  sello_salida: "",
  sello_salida_2: "",
  condicion_salida: "",
  destino: "",
  guardia_salida_nombre: "",
  firma_guardia_salida: "",
  firma_chofer_salida: "",
  sello_vvtt_estado: "",
  sello_vvtt_foto: "",
  pallets: "",
  cajas: "",
  bultos: "",
  hora_apertura_cortina: "",
  hora_cierre_cortina: "",
  foto_unidad_salida: "",
  foto_sello_salida: "",
};

export default function SirucRecordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showExit, setShowExit] = useState(false);
  const [guardiaOtroSalida, setGuardiaOtroSalida] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [exit, setExit] = useState(EMPTY_EXIT);
  const [entryForm, setEntryForm] = useState({});
  const [downloading, setDownloading] = useState(false);

  const { data: record, isLoading } = useQuery({
    queryKey: ["siruc-record", id],
    queryFn: () => api.get(`/siruc/records/${id}`).then((r) => {
      setEntryForm(r.data.entry || {});
      setExit(r.data.exit || EMPTY_EXIT);
      return r.data;
    }),
  });

  const updateMutation = useMutation({
    mutationFn: (body) => api.patch(`/siruc/records/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-record", id] });
      toast.success("Registro actualizado");
      feedback.success();
      setShowEditEntry(false);
      setShowExit(false);
    },
    onError: (e) => { toast.error(apiError(e, "Error al actualizar")); feedback.error(); },
  });

  const deleteInspectionMutation = useMutation({
    mutationFn: (inspId) => api.delete(`/siruc/inspections/${inspId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-record", id] });
      toast.success("Inspección eliminada");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar inspección")),
  });

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);

      if (window.Capacitor?.isNativePlatform()) {
        const token = localStorage.getItem("sisp_token");
        const url = `${api.defaults.baseURL}siruc/records/${id}/pdf?token=${token}`;
        window.open(url, '_blank');
        return;
      }

      const res = await api.get(`/siruc/records/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `REPORTE_CONSOLIDADO_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar reporte");
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) return <div className="p-8 text-muted-foreground">Cargando…</div>;
  if (!record) return <div className="p-8 text-muted-foreground">Registro no encontrado.</div>;

  const e = record.entry || {};
  const x = record.exit || {};

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <button
        onClick={() => navigate("/siruc/records")}
        data-testid={SIRUC_RECORDS.detailBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-navy mb-4"
      >
        <ArrowLeft size={16} /> Volver a registros
      </button>

      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-navy">
            {e.placas_unidad || "Registro"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Creado por {record.created_by} · {new Date(record.created_at).toLocaleString("es-MX")}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 h-10 px-4 border border-navy text-navy font-medium hover:bg-navy hover:text-white transition-colors disabled:opacity-50"
          >
            <FileDown size={16} /> {downloading ? "Generando..." : "Reporte Consolidado"}
          </button>
          {record.drive_report_link && (
            <a
              href={record.drive_report_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 h-10 px-4 bg-navy/5 border border-navy/20 text-navy font-bold text-xs uppercase hover:bg-navy/10 transition-colors"
            >
              <ExternalLink size={16} /> Ver en Drive
            </a>
          )}
          <Link
            to={`/siruc/inspections/new?record_id=${record.id}&placas=${encodeURIComponent(e.placas_unidad || "")}`}
            data-testid={SIRUC_RECORDS.detailAddInspection}
            className="flex items-center gap-2 h-10 px-4 border border-navy text-navy font-medium hover:bg-navy hover:text-white transition-colors"
          >
            <ClipboardCheck size={16} /> Inspección
          </Link>
          {record.status !== "salida" && (
            <button
              onClick={() => setShowExit(true)}
              data-testid={SIRUC_RECORDS.detailExitButton}
              className="flex items-center gap-2 h-10 px-4 bg-success text-white font-medium hover:bg-success-hover"
            >
              <Truck size={16} /> Registrar salida
            </button>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        <section className="bg-white border border-border p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-navy">Datos de entrada</h2>
            <button
              onClick={() => setShowEditEntry(true)}
              className="text-xs font-bold text-navy border-b border-navy hover:text-navy-hover"
            >
              MODIFICAR ENTRADA
            </button>
          </div>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Tipo de unidad" value={e.tipo_unidad} />
            <Detail label="Placas" value={e.placas_unidad} />
            <Detail label="Chofer" value={e.chofer_nombre} />
            <Detail label="Licencia" value={e.licencia_conductor} />
            <Detail label="Compañía" value={e.compania_transporte} />
            <Detail label="Número de tractor" value={e.numero_tractor} />
            <Detail label="Número de caja" value={e.numero_caja} />
            <Detail label="Sello de entrada" value={e.sello_entrada} />
            <Detail label="Destino" value={e.destino} />
            <Detail label="Guardia de caseta" value={e.guardia_caseta_nombre} />
            <Detail label="Descripción de carga" value={e.descripcion_carga} full />
          </dl>

          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <PhotoView label="Frente" src={e.foto_frente_unidad} />
            <PhotoView label="Atrás" src={e.foto_atras_caja} />
            <PhotoView label="ID chofer" src={e.foto_id_chofer} />
          </div>
        </section>

        <section className="bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-navy">Salida</h2>
            {record.exit && (
              <button
                onClick={() => setShowExit(true)}
                className="text-xs font-bold text-navy border-b border-navy hover:text-navy-hover"
              >
                MODIFICAR
              </button>
            )}
          </div>
          {record.exit ? (
            <dl className="space-y-3 text-sm">
              <Detail label="Cortina" value={x.cortina_salida} />
              <Detail label="Sello de salida" value={x.sello_salida} />
              <Detail label="Condición" value={x.condicion_salida} />
              <Detail label="Destino" value={x.destino} />
              <Detail label="Guardia de salida" value={x.guardia_salida_nombre} />
              <Detail label="Fecha de salida" value={x.fecha_salida ? new Date(x.fecha_salida).toLocaleString("es-MX") : ""} />
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Sin salida registrada.</p>
          )}
        </section>
      </div>

      <section className="bg-white border border-border p-5 mt-4">
        <h2 className="font-heading text-xl font-bold text-navy mb-4">Inspecciones vinculadas</h2>
        {record.inspections?.length ? (
          <ul className="divide-y divide-border">
            {record.inspections.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-navy">
                    {i.inspection_type === "9_puntos" ? "9 puntos" : "19 puntos"} · {i.placas_unidad}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(i.created_at).toLocaleString("es-MX")} · {i.created_by}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 ${
                    i.status_general === "bueno" ? "bg-success/10 text-success" : "bg-critical/10 text-critical"
                  }`}>
                    {i.status_general === "bueno" ? "Bueno" : "Malo"}
                  </span>
                  <button
                    onClick={() => {
                      if(confirm("¿Eliminar esta inspección?")) deleteInspectionMutation.mutate(i.id);
                    }}
                    className="text-critical/60 hover:text-critical p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Sin inspecciones vinculadas.</p>
        )}
      </section>

      {showEditEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-xl font-bold text-navy mb-4">Modificar entrada</h3>
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                updateMutation.mutate({ entry: entryForm });
              }}
              className="grid grid-cols-2 gap-4"
            >
              <Field label="Placas"><input value={entryForm.placas_unidad} onChange={e => setEntryForm({...entryForm, placas_unidad: e.target.value})} className="input" /></Field>
              <Field label="Chofer"><input value={entryForm.chofer_nombre} onChange={e => setEntryForm({...entryForm, chofer_nombre: e.target.value})} className="input" /></Field>
              <Field label="Compañía"><input value={entryForm.compania_transporte} onChange={e => setEntryForm({...entryForm, compania_transporte: e.target.value})} className="input" /></Field>
              <Field label="Tractor"><input value={entryForm.numero_tractor} onChange={e => setEntryForm({...entryForm, numero_tractor: e.target.value})} className="input" /></Field>
              <Field label="Caja"><input value={entryForm.numero_caja} onChange={e => setEntryForm({...entryForm, numero_caja: e.target.value})} className="input" /></Field>
              <Field label="Sello"><input value={entryForm.sello_entrada} onChange={e => setEntryForm({...entryForm, sello_entrada: e.target.value})} className="input" /></Field>
              <div className="col-span-2 flex gap-3 pt-4">
                <button type="submit" className="h-11 px-6 bg-navy text-white font-bold">GUARDAR CAMBIOS</button>
                <button type="button" onClick={() => setShowEditEntry(false)} className="h-11 px-6 border border-border">CANCELAR</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border w-full max-w-lg p-6">
            <h3 className="font-heading text-xl font-bold text-navy mb-4">
              {record.exit ? "Modificar salida" : "Registrar salida"}
            </h3>
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                updateMutation.mutate({ exit: exit, status: "salida" });
              }}
              className="space-y-4"
            >
              <Field label="Cortina de salida">
                <input
                  data-testid={SIRUC_RECORDS.exitCortina}
                  value={exit.cortina_salida}
                  onChange={(ev) => setExit({ ...exit, cortina_salida: ev.target.value })}
                  className="input"
                />
              </Field>
              <Field label={`Sello de salida${e.tipo_unidad === "full" ? " 1" : ""}`}>
                <input
                  data-testid={SIRUC_RECORDS.exitSello}
                  value={exit.sello_salida}
                  onChange={(ev) => setExit({ ...exit, sello_salida: ev.target.value })}
                  className="input"
                />
              </Field>
              {e.tipo_unidad === "full" && (
                <Field label="Sello de salida 2">
                  <input
                    value={exit.sello_salida_2}
                    onChange={(ev) => setExit({ ...exit, sello_salida_2: ev.target.value })}
                    className="input"
                  />
                </Field>
              )}
              <Field label="Condición de salida">
                <div className="flex flex-wrap gap-2" data-testid={SIRUC_RECORDS.exitCondicion}>
                  {CONDICIONES_SALIDA.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setExit({ ...exit, condicion_salida: opt })}
                      className={`px-3 py-1.5 text-xs font-bold border ${
                        exit.condicion_salida === opt ? "bg-navy text-white border-navy" : "bg-white text-navy border-border"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Pallets">
                  <input
                    value={exit.pallets}
                    onChange={(ev) => setExit({ ...exit, pallets: ev.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="Cajas">
                  <input
                    value={exit.cajas}
                    onChange={(ev) => setExit({ ...exit, cajas: ev.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="Bultos">
                  <input
                    value={exit.bultos}
                    onChange={(ev) => setExit({ ...exit, bultos: ev.target.value })}
                    className="input"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Sello VVTT Estado">
                  <input
                    value={exit.sello_vvtt_estado}
                    onChange={(ev) => setExit({ ...exit, sello_vvtt_estado: ev.target.value })}
                    className="input"
                    placeholder="Ver, Verificar, Tirar, Torcer"
                  />
                </Field>
                <div className="col-span-2">
                  <PhotoCapture
                    label="Foto Sello VVTT"
                    single
                    value={exit.sello_vvtt_foto ? [exit.sello_vvtt_foto] : []}
                    onChange={(v) => setExit({ ...exit, sello_vvtt_foto: v[0] || "" })}
                    testid="siruc-exit-vvtt-foto"
                  />
                </div>
                <Field label="Hora Apertura Cortina">
                  <input
                    type="time"
                    value={exit.hora_apertura_cortina}
                    onChange={(ev) => setExit({ ...exit, hora_apertura_cortina: ev.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="Hora Cierre Cortina">
                  <input
                    type="time"
                    value={exit.hora_cierre_cortina}
                    onChange={(ev) => setExit({ ...exit, hora_cierre_cortina: ev.target.value })}
                    className="input"
                  />
                </Field>
              </div>

              <Field label="Destino">
                <input
                  data-testid={SIRUC_RECORDS.exitDestino}
                  value={exit.destino}
                  onChange={(ev) => setExit({ ...exit, destino: ev.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Guardia de salida">
                {!guardiaOtroSalida ? (
                  <select
                    data-testid={SIRUC_RECORDS.exitGuardia}
                    value={GUARDIA_OPCIONES.includes(exit.guardia_salida_nombre) ? exit.guardia_salida_nombre : ""}
                    onChange={(ev) => {
                      if (ev.target.value === "__otro__") { setGuardiaOtroSalida(true); setExit({ ...exit, guardia_salida_nombre: "" }); }
                      else setExit({ ...exit, guardia_salida_nombre: ev.target.value });
                    }}
                    className="input"
                  >
                    <option value="" disabled>Selecciona...</option>
                    {GUARDIA_OPCIONES.map((g) => <option key={g} value={g}>{g}</option>)}
                    <option value="__otro__">Otro...</option>
                  </select>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={exit.guardia_salida_nombre}
                      onChange={(ev) => setExit({ ...exit, guardia_salida_nombre: ev.target.value })}
                      className="input flex-1"
                    />
                    <button type="button" onClick={() => setGuardiaOtroSalida(false)} className="text-[10px] font-black uppercase text-navy/60 whitespace-nowrap">Lista</button>
                  </div>
                )}
              </Field>

              <PhotoCapture
                label="Foto Unidad (Salida)"
                single
                value={exit.foto_unidad_salida ? [exit.foto_unidad_salida] : []}
                onChange={(v) => setExit({ ...exit, foto_unidad_salida: v[0] || "" })}
                testid="siruc-exit-foto-unidad"
              />
              <PhotoCapture
                label="Foto Sello (Salida)"
                single
                value={exit.foto_sello_salida ? [exit.foto_sello_salida] : []}
                onChange={(v) => setExit({ ...exit, foto_sello_salida: v[0] || "" })}
                testid="siruc-exit-foto-sello"
              />

              <div className="max-w-sm">
                <SignaturePad
                  label="Firma Guardia (Salida) *"
                  value={exit.firma_guardia_salida}
                  onChange={(v) => setExit({ ...exit, firma_guardia_salida: v })}
                  testid="siruc-firma-guardia-sal"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid={SIRUC_RECORDS.exitSubmit}
                  disabled={updateMutation.isPending}
                  className="h-11 px-5 bg-success text-white font-semibold hover:bg-success-hover disabled:opacity-50"
                >
                  {updateMutation.isPending ? "Guardando…" : "Confirmar"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowExit(false)}
                  className="h-11 px-5 border border-border text-navy hover:bg-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-navy font-medium mt-0.5">{value || "—"}</dd>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-navy">{label}</label>
      {children}
    </div>
  );
}

function PhotoView({ label, src }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">{label}</p>
      {src ? (
        <img src={src} alt={label} className="w-full aspect-video object-cover border border-border" />
      ) : (
        <div className="w-full aspect-video border border-dashed border-border flex items-center justify-center text-muted-foreground">
          <Camera size={20} />
        </div>
      )}
    </div>
  );
}
